import React, { useState, useEffect } from 'react';
import { Zap, X, Radio, ArrowRight } from 'lucide-react';

// Sticky top bar shown site-wide when a flash sale is active.
// If the sale is flagged "live" with a TikTok URL, it switches to a red/pink
// "EN DIRECT sur TikTok" style with a button linking to the live.
const LiveFlashBar = () => {
  const [sale, setSale] = useState(null);
  const [timeLeft, setTimeLeft] = useState({ hours: 0, minutes: 0, seconds: 0 });
  const [dismissedId, setDismissedId] = useState(() => {
    try { return sessionStorage.getItem('liveFlashBarDismissed'); } catch { return null; }
  });

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    let cancelled = false;
    const fetchSale = async () => {
      try {
        const res = await fetch(`${API_URL}/api/flash-sale`);
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        setSale(data.active ? data : null);
      } catch (e) { /* ignore network errors */ }
    };
    fetchSale();
    const interval = setInterval(fetchSale, 60 * 1000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [API_URL]);

  useEffect(() => {
    if (!sale?.end_time) return;
    const compute = () => {
      const diff = new Date(sale.end_time).getTime() - Date.now();
      if (diff <= 0) { setSale(null); return { hours: 0, minutes: 0, seconds: 0 }; }
      return {
        hours: Math.floor(diff / 3600000),
        minutes: Math.floor((diff % 3600000) / 60000),
        seconds: Math.floor((diff % 60000) / 1000),
      };
    };
    setTimeLeft(compute());
    const t = setInterval(() => setTimeLeft(compute()), 1000);
    return () => clearInterval(t);
  }, [sale]);

  if (!sale || dismissedId === sale.id) return null;

  const isLive = sale.live && sale.tiktok_url;
  const pad = (n) => String(n).padStart(2, '0');

  const dismiss = () => {
    setDismissedId(sale.id);
    try { sessionStorage.setItem('liveFlashBarDismissed', sale.id); } catch { /* noop */ }
  };

  return (
    <div
      data-testid="live-flash-bar"
      className={`sticky top-14 lg:top-16 z-30 w-full border-b backdrop-blur-md ${
        isLive
          ? 'bg-gradient-to-r from-[#1a0710] via-[#2a0a18] to-[#1a0710] border-pink-500/40'
          : 'bg-gradient-to-r from-[#04141a] via-[#062028] to-[#04141a] border-cyan-500/40'
      }`}
    >
      <div className="mx-auto max-w-6xl px-3 md:px-5 py-2 flex items-center gap-3 md:gap-5">
        {/* Left: status badge */}
        {isLive ? (
          <span data-testid="live-badge" className="inline-flex items-center gap-1.5 shrink-0 px-2.5 py-1 rounded-full bg-red-600 text-white text-[11px] md:text-xs font-extrabold uppercase tracking-wider shadow-[0_0_18px_-4px_rgba(244,63,94,0.9)]">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full rounded-full bg-white opacity-75 animate-ping" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-white" />
            </span>
            EN DIRECT
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 shrink-0 px-2.5 py-1 rounded-full bg-cyan-500/15 border border-cyan-400/40 text-cyan-300 text-[11px] md:text-xs font-bold uppercase tracking-wider">
            <Zap className="w-3.5 h-3.5" />Vente Flash
          </span>
        )}

        {/* Middle: message + discount */}
        <div className="flex items-center gap-2 md:gap-3 min-w-0 flex-1">
          <span className={`text-lg md:text-2xl font-black shrink-0 ${isLive ? 'text-pink-300' : 'text-cyan-300'}`}>
            -{sale.discount_percent}%
          </span>
          <span className="text-gray-200 text-xs md:text-sm truncate">
            {sale.banner_text ? (
              <span className="font-semibold text-white">{sale.banner_text}</span>
            ) : isLive ? (
              <>Promo <span className="font-semibold text-white">live</span> — {sale.title} · profites-en pendant le direct&nbsp;!</>
            ) : (
              <><span className="font-semibold text-white">{sale.title}</span> — réduction sur le site, offre limitée&nbsp;!</>
            )}
          </span>
        </div>

        {/* Timer */}
        <div className={`hidden md:flex items-center gap-1.5 shrink-0 font-mono text-sm rounded-md px-2.5 py-1 ${timeLeft.hours === 0 ? 'text-red-300 bg-red-950/50 ring-1 ring-red-500/60 animate-pulse' : 'text-white bg-black/40'}`} data-testid="live-flash-timer">
          <span>{pad(timeLeft.hours)}</span>:<span>{pad(timeLeft.minutes)}</span>:<span>{pad(timeLeft.seconds)}</span>
        </div>

        {/* CTA */}
        {isLive ? (
          <a
            href={sale.tiktok_url}
            target="_blank"
            rel="noreferrer"
            data-testid="watch-live-btn"
            className="shrink-0 inline-flex items-center gap-1.5 px-3 md:px-4 py-1.5 rounded-full bg-white text-[#1a0710] font-bold text-xs md:text-sm hover:scale-[1.03] transition-transform shadow-[0_0_20px_-4px_rgba(255,255,255,0.5)]"
          >
            <Radio className="w-3.5 h-3.5 md:w-4 md:h-4" /><span className="hidden sm:inline">Regarder le live TikTok</span><span className="sm:hidden">Live TikTok</span>
          </a>
        ) : (
          <a
            href="/services"
            data-testid="flash-cta-btn"
            className="shrink-0 inline-flex items-center gap-1.5 px-3 md:px-4 py-1.5 rounded-full font-bold text-xs md:text-sm text-[#06181a] hover:scale-[1.03] transition-transform"
            style={{ background: 'linear-gradient(90deg,#14b8c8,#1de9d0)' }}
          >
            J'en profite<ArrowRight className="w-3.5 h-3.5 md:w-4 md:h-4" />
          </a>
        )}

        {/* Dismiss */}
        <button onClick={dismiss} data-testid="live-flash-dismiss" title="Masquer" className="shrink-0 p-1 text-gray-400 hover:text-white transition-colors">
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default LiveFlashBar;
